import { formatMoney, SIZE_LABELS, REPAIR_LABELS } from "@/lib/pricing";

export const fmtDateTime = (iso) =>
  iso ? new Date(iso).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "—";

export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "—";

// Split a saved quote doc into panel lines and service lines (legacy docs mixed them in line_items)
export function splitSavedQuote(qt) {
  const items = qt.line_items || [];
  const panels = items.filter((x) => x.respray !== undefined);
  const services = qt.services?.length ? qt.services : items.filter((x) => x.price !== undefined && x.respray === undefined);
  return { panels, services };
}

// WhatsApp formatting: *bold*, _italic_. Sections are separated by blank lines so long quotes stay readable.
const B = (t) => `*${t}*`;
const IND = "    "; // indent for detail lines under an item
const money = (sym, n) => formatMoney(sym, n).replace(/\.00$/, "");

function headerLines(doc) {
  const { shop } = doc;
  const lines = [B(shop.name.toUpperCase()), `_${shop.tagline}_`];
  if (shop.address) lines.push(shop.address);
  const contact = [shop.phone, shop.email].filter(Boolean).join(" · ");
  if (contact) lines.push(contact);
  lines.push("");
  const paid = doc.kind === "invoice" && doc.status === "paid";
  const title = paid ? "RECEIPT 收据 · PAID 已付款" : doc.kind === "invoice" ? "INVOICE 发票" : "QUOTATION 报价单";
  lines.push(`${B(title)}${doc.ref && doc.ref !== "DRAFT" ? `  ${doc.ref}` : ""}`);
  lines.push(`Date 日期: ${fmtDate(doc.created_at)}`);
  if (paid) lines.push(`Payment received 收款日期: ${fmtDate(doc.paidAt || doc.created_at)}`);
  else if (doc.dueDate) lines.push(`Due 到期: ${fmtDate(doc.dueDate)} (${doc.termsDays}-day terms)`);
  return lines;
}

function partyLines(doc) {
  const { customer, vehicle } = doc;
  const lines = ["", B("Customer 客户")];
  lines.push(customer.name ? `${customer.name}${customer.phone ? ` · ${customer.phone}` : ""}` : "Walk-in 临时客户");
  if (vehicle.plate || vehicle.model) lines.push([vehicle.plate, vehicle.model].filter(Boolean).join(" · "));
  const size = SIZE_LABELS[doc.size];
  const meta = [vehicle.paintCode ? `Paint code 色号 ${vehicle.paintCode}` : "", size ? `${size.en} ${size.zh}` : ""].filter(Boolean);
  if (meta.length) lines.push(meta.join(" · "));
  return lines;
}

function panelLines(doc, sym) {
  if (!doc.panels.length) return [];
  const lines = ["", `${B("Respray 喷漆")}  (${doc.panels.length} ${doc.panels.length === 1 ? "panel" : "panels"})`];
  doc.panels.forEach((it, i) => {
    const qtyTxt = it.qty > 1 ? ` ×${it.qty}` : "";
    const name = `${i + 1}. ${[it.en, it.zh].filter(Boolean).join(" ")}${qtyTxt}`;
    const details = [];
    if (it.sprayMode === "touchup") details.push(`Touch-up 局部补漆 ${money(sym, it.respray)}`);
    else if (it.repair > 0 || it.special > 0) details.push(`Respray 喷漆 ${money(sym, it.respray)}`);
    if (it.repair > 0) details.push(`${REPAIR_LABELS[it.repairTier]?.short || ""} repair 修整 +${money(sym, it.repair)}`.trim());
    if (it.special > 0) details.push(`Tricoat 三层漆 +${money(sym, it.special)}`);
    if (!details.length) { lines.push(`${name} — ${B(money(sym, it.total))}`); return; } // plain respray: one line
    lines.push(name, ...details.map((d) => IND + d), `${IND}= ${B(money(sym, it.total))}`);
  });
  return lines;
}

function serviceLines(doc, sym) {
  if (!doc.services.length) return [];
  const lines = ["", B("Services 服务")];
  doc.services.forEach((s) => {
    if (s.kind === "full_respray") { lines.push(`• ${s.en} — ${B(money(sym, s.price))}`); if (s.zh) lines.push(IND + s.zh); return; }
    lines.push(`• ${[s.en, s.zh].filter(Boolean).join(" ")} — ${B(money(sym, s.price))}`);
  });
  return lines;
}

function partLines(doc, sym) {
  const parts = doc.parts || [];
  if (!parts.length) return [];
  const lines = ["", B("Spare Parts 零件")];
  parts.forEach((p) => {
    lines.push(`• ${p.name} — ${B(money(sym, p.total))}`);
    if (p.ri > 0) lines.push(`${IND}part ${money(sym, p.price)} + R&I ${money(sym, p.ri)}`);
  });
  return lines;
}

function totalLines(doc, sym) {
  const b = doc.breakdown || {};
  const lines = ["", B("Summary 小计")];
  if (b.respray > 0) lines.push(`Respray 喷漆: ${money(sym, b.respray)}`);
  if (b.repair > 0) lines.push(`Panel beating & putty 钣金补土: ${money(sym, b.repair)}`);
  if (b.special > 0) lines.push(`Tricoat upgrade 三层漆: ${money(sym, b.special)}`);
  if (b.services > 0) lines.push(`Services 服务: ${money(sym, b.services)}`);
  lines.push(`Subtotal 小计: ${money(sym, doc.subtotal)}`);
  if (doc.discount?.amount > 0) {
    const lbl = doc.discount.mode === "percent" ? `${doc.discount.value}%` : "manual";
    lines.push(`Discount 折扣 (${lbl}): -${money(sym, doc.discount.amount)}`);
  }
  if (b.parts > 0) lines.push(`Spare parts 零件 (net): ${money(sym, b.parts)}`);
  if (doc.tax > 0) lines.push(`Tax/GST 税费${doc.taxRate !== undefined ? ` (${doc.taxRate}%)` : ""}: ${money(sym, doc.tax)}`);
  lines.push("", B(`TOTAL 合计: ${formatMoney(sym, doc.total)}`));
  return lines;
}

// Plain-text version for WhatsApp / clipboard
export function quoteDocText(doc) {
  const sym = doc.shop.sym;
  const lines = [...headerLines(doc), ...partyLines(doc), ...panelLines(doc, sym), ...serviceLines(doc, sym), ...partLines(doc, sym), ...totalLines(doc, sym)];
  if (doc.kind === "invoice" && doc.status === "paid") lines.push("", B("Payment received — thank you! 款已收到，谢谢！"), "_We appreciate your business and look forward to serving you again. 感谢您的支持，期待再次为您服务。_");
  else if (doc.kind === "invoice") { if (doc.shop.bankDetails) lines.push("", B("Payment Method 付款方式"), doc.shop.bankDetails); if (doc.shop.invoiceFooter) lines.push("", `_${doc.shop.invoiceFooter}_`); }
  else if (doc.shop.footer) lines.push("", `_${doc.shop.footer}_`);
  return lines.join("\n");
}

export function statementText(doc) {
  const sym = doc.shop.sym;
  const lines = [B(doc.shop.name.toUpperCase()), B(`Statement of Account 对账单`), `Date 日期: ${fmtDate(doc.statementDate)}`, "", B("For 客户"), doc.customer.name, "", `${B("Unpaid Invoices 未付发票")}  (${doc.invoices.length})`];
  doc.invoices.forEach((i, n) => {
    lines.push(`${n + 1}. ${i.invoice_no} — ${B(money(sym, i.total))}`);
    lines.push(`${IND}due ${fmtDate(i.due_date)}${i.overdue ? ` · overdue ${i.days_overdue}d` : ""}`);
  });
  lines.push("", B(`TOTAL DUE 应付总额: ${formatMoney(sym, doc.totalDue)}`));
  if (doc.shop.bankDetails) lines.push("", B("Payment Method 付款方式"), doc.shop.bankDetails);
  lines.push("", "_Please quote the invoice number(s) when paying. Thank you 谢谢!_");
  return lines.join("\n");
}
